import { assign, createMachine } from "xstate";
import { invalidPostCodes } from "@nju/utils";

export interface IContext {
  orchestratorId?: string;
  street: string;
  streetNumber: string;
  apartmentNumber: string;
  postCode: string;
  municipality: string;
  country: string;
  contactNumber: string;
  note?: string;
  withoutStreet: boolean;
  firstNameError?: string;
  lastNameError?: string;
  streetError?: string;
  streetNumberError?: string;
  apartmentNumberError?: string;
  postCodeError?: string;
  municipalityError?: string;
  contactNumberError?: string;
  formError?: string;
}

type IEvent =
  | {
      type: "SET_INPUT_VALUE";
      name: string;
      value: string;
    }
  | {
      type: "SET_PHONE_NUMBER_VALUE";
      value: string;
    }
  | {
      type: "SET_POSTCODE_VALUE";
      value: string;
    }
  | {
      type: "SET_TEXT_VALUE";
      value: string;
    }
  | {
      type: "SET_WITHOUT_STREET";
      value: boolean;
    }
  | { type: "SUBMIT" };

export const changeSimDeliveryAddressMachine = createMachine(
  {
    context: {
      street: "",
      streetNumber: "",
      apartmentNumber: "",
      postCode: "",
      municipality: "",
      country: "Polska",
      contactNumber: "",
      note: "",
      withoutStreet: false,
    },
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    initial: "ready",
    states: {
      ready: {
        on: {
          SET_INPUT_VALUE: {
            actions: "setInputValue",
          },
          SET_PHONE_NUMBER_VALUE: {
            actions: "setPhoneNumberValue",
          },
          SET_POSTCODE_VALUE: {
            actions: "setPostCodeValue",
          },
          SET_TEXT_VALUE: {
            actions: "setTextValue",
          },
          SET_WITHOUT_STREET: {
            actions: "setWithoutStreet",
          },
          SUBMIT: {
            actions: "clearFormError",
            target: "validate",
          },
        },
      },
      validate: {
        entry: "validateForm",
        always: [
          {
            cond: "hasNoInputErrors",
            actions: "setDeliveryAddress",
            target: "final",
          },
          {
            target: "ready",
          },
        ],
      },
      final: {
        type: "final",
      },
    },
  },
  {
    guards: {
      hasNoInputErrors: ({
        streetError,
        streetNumberError,
        apartmentNumberError,
        postCodeError,
        municipalityError,
        contactNumberError,
      }) => {
        return (
          streetError === undefined &&
          streetNumberError === undefined &&
          apartmentNumberError === undefined &&
          postCodeError === undefined &&
          municipalityError === undefined &&
          contactNumberError === undefined
        );
      },
    },
    actions: {
      clearFormError: assign<IContext, IEvent>(() => {
        return {
          formError: undefined,
        };
      }),
      validateForm: assign<IContext, IEvent>({
        streetError: ({ street, withoutStreet }) => {
          if (street.trim() === "" && !withoutStreet) {
            return "Wpisz ulicę.";
          }

          if (street.length > 35) {
            return "Nazwa ulicy jest za długa.";
          }

          if (/[!"#$%&'()*+,.:;<=>?@[\\\]^_{|}]+/.test(street)) {
            return "Podaj ulicę w prawidłowym formacie.";
          }

          return;
        },
        streetNumberError: ({ streetNumber }) => {
          if (streetNumber.trim() === "") {
            return "Wpisz numer domu.";
          }

          if (streetNumber.replace(/ /g, "").length > 13) {
            return "Numer jest za długi.";
          }

          if (/[!"#$%&'()*+,.:;<=>?@[\\\]^_{|}]+/.test(streetNumber)) {
            return "Podaj numer w prawidłowym formacie.";
          }

          return;
        },
        apartmentNumberError: ({ apartmentNumber }) => {
          if (apartmentNumber.replace(/ /g, "").length > 10) {
            return "Numer jest za długi.";
          }

          if (/[!"#$%&'()*+,.:;<=>?@[\\\]^_{|}]+/.test(apartmentNumber)) {
            return "Podaj numer lokalu w prawidłowym formacie.";
          }

          return;
        },
        postCodeError: ({ postCode }) => {
          if (postCode.trim() === "" || !/^\d{2}-\d{3}$/.test(postCode)) {
            return "Wpisz kod pocztowy w formacie XX-XXX.";
          }

          if (invalidPostCodes.includes(postCode)) {
            return "Wpisz poprawny kod pocztowy.";
          }

          return;
        },
        municipalityError: ({ municipality }) => {
          if (municipality.trim() === "") {
            return "Wpisz miasto.";
          }

          if (/[!"#$%&'()*+,.:;<=>?@[\\\]^_{|}]+/.test(municipality)) {
            return "Podaj nazwę miasta w prawidłowym formacie.";
          }

          if (municipality.replace(/ /g, "").length > 35) {
            return "Nazwa miasta jest za długa.";
          }

          return;
        },
        contactNumberError: ({ contactNumber }) => {
          if (contactNumber.trim() === "") {
            return "Wpisz numer telefonu komórkowego.";
          }

          if (contactNumber.replace(/ /g, "").length !== 9) {
            return "Numer musi się składać z 9 cyfr.";
          }

          return;
        },
      }),
      setInputValue: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_INPUT_VALUE") {
          return {
            [event.name]: event.value,
            formError: undefined,
          };
        }

        return context;
      }),
      setPhoneNumberValue: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_PHONE_NUMBER_VALUE") {
          return {
            contactNumber: event.value,
            formError: undefined,
          };
        }

        return context;
      }),
      setPostCodeValue: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_POSTCODE_VALUE") {
          return {
            postCode: event.value,
            formError: undefined,
          };
        }

        return context;
      }),
      setTextValue: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_TEXT_VALUE") {
          return {
            note: event.value,
            formError: undefined,
          };
        }

        return context;
      }),
      setWithoutStreet: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_WITHOUT_STREET") {
          return {
            streetError: event.value ? undefined : context.streetError,
            withoutStreet: event.value,
            street: "",
            formError: undefined,
          };
        }

        return context;
      }),
    },
  },
);
